const JWT_LIKE_RE = /\beyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9._-]{10,}\.[a-zA-Z0-9._-]{10,}\b/g

function scrubSnippet(x: any): string {
  const s = String(x || '')
  if (!s) return ''
  return s
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 220)
    .replace(JWT_LIKE_RE, '<jwt>')
    .replace(/([?&])(token|auth|key|signature|hash)=[^&#\s]+/gi, '$1$2=<redacted>')
}

function isTextLikeContentType(ct: string): boolean {
  const x = String(ct || '').toLowerCase()
  return x.includes('text/html') || x.startsWith('text/') || x.includes('application/xhtml+xml')
}

function isJsonLikeContentType(ct: string): boolean {
  const x = String(ct || '').toLowerCase()
  return x.includes('application/json') || x.includes('+json') || x.includes('text/json')
}

function sleep(ms: number): Promise<void> {
  const t = Math.max(0, Number(ms || 0))
  return new Promise((r) => setTimeout(r, t))
}

function withDefaultPublicInit(init?: RequestInit): RequestInit {
  // Public media/asset fetches must never send cookies.
  // This avoids coupling with admin cookies and reduces nginx "header too large" / transient 400 issues.
  return {
    credentials: 'omit',
    ...(init || {}),
    // If caller explicitly sets credentials, it overrides the default.
  }
}

/**
 * fetchPublicAsset:
 * - низкоуровневый fetch() для публичных ресурсов (assets/media/locales/scenarios)
 * - credentials:'omit' по умолчанию
 * - 1 безопасный retry на transient 400/5xx и/или mismatch по ожидаемому content-type
 *
 * Возвращает Response только если ответ OK и (если задано) content-type ожидаемый.
 */
export async function fetchPublicAsset(
  url: string,
  init?: RequestInit,
  ctx?: { expected_content_type?: 'json' | 'video' | 'text'; stage?: string; attempts?: number; backoffMs?: number; [k: string]: any },
): Promise<Response> {
  const attempts = Math.max(1, Number(ctx?.attempts ?? 2))
  const backoffMs = Math.max(0, Number(ctx?.backoffMs ?? 150))
  const stage = String(ctx?.stage || 'asset')

  let lastErr: any = null
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const res = await fetch(url, withDefaultPublicInit(init))
    const status = Number((res as any)?.status || 0) || 0
    const ctRaw = String(res.headers?.get?.('content-type') || '')
    const ct = ctRaw.slice(0, 120)

    const ctLower = ctRaw.toLowerCase()
    const looksText = isTextLikeContentType(ctLower)
    const looksJson = isJsonLikeContentType(ctLower)
    const looksVideo = ctLower.includes('video/')

    const expected = ctx?.expected_content_type
    const mismatch =
      (expected === 'json' && !looksJson)
      || (expected === 'video' && !looksVideo)
      || (expected === 'text' && !looksText)

    const shouldRetry =
      attempt < attempts
      && (
        status === 400
        || (!res.ok && status >= 500)
        || mismatch
      )

    if (!res.ok || mismatch) {
      let bodySnippet: string | null = null
      try {
        if (looksText) bodySnippet = scrubSnippet(await res.text()) || null
      } catch {}
      const e: any = new Error(
        !res.ok
          ? `Public fetch failed (status=${status || 'unknown'}): ${url}`
          : `Public fetch content-type mismatch (ct=${ct || 'unknown'} status=${status || 'unknown'}): ${url}`,
      )
      e.telemetry = {
        stage,
        url,
        status: status || null,
        content_type: ct || null,
        body_snippet: bodySnippet,
        attempt,
        ...(ctx || {}),
      }
      lastErr = e
      if (shouldRetry) {
        await sleep(backoffMs)
        continue
      }
      throw e
    }

    return res
  }

  if (lastErr) throw lastErr
  throw new Error(`Public asset fetch failed: ${url}`)
}
